import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import supabase from "../../supabaseClient";
import './Groups.css';
import GroupsSidebar from './Components/GroupsSidebar';
import GroupMainFeed from './Components/GroupMainFeed';
import GroupView from './Components/GroupView';

const Groups = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [groups, setGroups] = useState([]);

  // Consolidated fetchGroups logic
  const fetchGroups = async (userId) => {
    if (!userId) return;
    try {
      // Step 1: Get group_ids where user is a member
      const { data: groupMembers, error: groupMembersError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', userId);
      if (groupMembersError) throw groupMembersError;
      const groupIds = (groupMembers || []).map(gm => gm.group_id);
      if (!groupIds.length) {
        setGroups([]);
        return;
      }
      // Step 2: Fetch group details for those group_ids
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          description,
          created_by,
          created_at,
          language,
          level,
          room_id,
          category,
          capacity,
          recurring,
          frequency,
          group_banner_url
        `)
        .in('id', groupIds);
      if (groupsError) throw groupsError;
      setGroups(groupsData || []);
    } catch (error) {
      setGroups([]);
    }
  };

  useEffect(() => {
    if (user?.id) fetchGroups(user.id);
  }, [user]);

  // Sync selectedGroup with the URL so /app/groups/:groupId deep-links open GroupView
  useEffect(() => {
    const match = location.pathname.match(/\/app\/groups\/([^/]+)/);
    const groupIdFromPath = match ? match[1] : null;

    if (!groupIdFromPath) {
      // No specific group in the URL: show the main groups list
      setSelectedGroup(null);
      setSelectedPostId(null);
      return;
    }

    if (selectedGroup === groupIdFromPath) {
      // Already showing this group
      return;
    }

    setSelectedGroup(groupIdFromPath);
    setSelectedPostId(null);
  }, [location.pathname, selectedGroup]);

  const handleGroupCreated = () => {
    if (user?.id) fetchGroups(user.id);
  };

  const handleSelectGroup = (groupId, postId = null) => {
    setSelectedGroup(groupId);
    setSelectedPostId(postId || null);
    if (groupId) {
      navigate(`/app/groups/${groupId}`);
    } else {
      navigate('/app/groups');
    }
  };

  const handleBack = () => {
    setSelectedGroup(null);
    setSelectedPostId(null);
    navigate('/app/groups');
  };

  return (
    <div className="groups-section">
      <div className="groups-panel page-panel">
        {!selectedGroup && (
          <>
            <GroupsSidebar
              groups={groups}
              selectedGroup={selectedGroup}
              onSelectGroup={handleSelectGroup}
              user={user}
              onGroupCreated={handleGroupCreated}
            />
            <div className="groups-main-content">
              <GroupMainFeed
                groups={groups}
                onSelectGroup={handleSelectGroup}
                user={user}
                onGroupCreated={handleGroupCreated}
              />
            </div>
          </>
        )}

        {selectedGroup && (
          <div className="groups-main-fullwidth">
            <GroupView
              groupId={selectedGroup}
              postId={selectedPostId}
              onBack={handleBack}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Groups;